import React, { useState, useEffect, useRef } from 'react';
import {
  componentList,
  defaultStyleGuide,
  generateCSSVariables,
  getSampleData,
  getHtmlTemplate,
  renderComponentDynamic
} from '../../data/component-templates';

const ComponentPreview = () => {
  const [styleGuide, setStyleGuide] = useState(defaultStyleGuide);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [previewMode, setPreviewMode] = useState('all'); // 'all' or 'single'
  const iframeRef = useRef(null);

  const generatePreviewHTML = (components) => {
    const cssVars = generateCSSVariables(styleGuide);

    // Reorder components: Header first, Footer last, others in between
    const orderedComponents = [...components];
    const headerIndex = orderedComponents.findIndex(c => c.component_code === 'HEADER_01');
    const footerIndex = orderedComponents.findIndex(c => c.component_code === 'FOOTER_01');

    let header = null;
    let footer = null;

    // Extract header and footer
    if (headerIndex !== -1) {
      header = orderedComponents.splice(headerIndex, 1)[0];
    }
    const newFooterIndex = orderedComponents.findIndex(c => c.component_code === 'FOOTER_01');
    if (newFooterIndex !== -1) {
      footer = orderedComponents.splice(newFooterIndex, 1)[0];
    }

    // Rebuild array: header first, others in middle, footer last
    const finalComponents = [];
    if (header) finalComponents.push(header);
    finalComponents.push(...orderedComponents);
    if (footer) finalComponents.push(footer);

    const componentsHTML = finalComponents.map(comp => {
      const content = comp.sample_data_json;
      return renderComponentDynamic(comp.component_code, content, styleGuide);
    }).join('\n');

    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Component Preview</title>
        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
        <link href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.0/font/bootstrap-icons.css" rel="stylesheet">
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Inter:wght@400;500;600;700&family=Manrope:wght@400;500;600;700;800&family=Outfit:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Poppins:wght@400;500;600;700&family=Sora:wght@400;500;600;700&display=swap" rel="stylesheet">
        <style>
          ${cssVars}
          body {
            margin: 0;
            padding: 0;
            background: var(--color-background);
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }

          /* Enhanced button styles */
          .btn {
            font-weight: var(--btn-font-weight);
            box-shadow: var(--btn-shadow);
            transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
            border: none;
          }
          .btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 24px 0 rgba(5, 150, 105, 0.35);
          }
          .btn:active {
            transform: translateY(0);
            box-shadow: 0 2px 8px 0 rgba(5, 150, 105, 0.25);
          }

          /* Enhanced input styles */
          .form-control {
            background: var(--input-bg);
            box-shadow: var(--input-shadow);
            transition: all 0.2s ease;
          }
          .form-control:focus {
            border-color: var(--input-focus-border);
            box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.12);
          }

          /* Smooth transitions for all interactive elements */
          a, button, input, select, textarea {
            transition: all 0.2s ease;
          }

          /* Link hover effect */
          a:hover {
            color: var(--color-primary);
          }
        </style>
      </head>
      <body>
        ${componentsHTML}
        <script src="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/js/bootstrap.bundle.min.js"></script>
      </body>
      </html>
    `;
  };

  useEffect(() => {
    if (iframeRef.current) {
      const components = previewMode === 'all'
        ? componentList
        : selectedComponent
          ? [selectedComponent]
          : [];

      const html = generatePreviewHTML(components);
      const blob = new Blob([html], { type: 'text/html' });
      iframeRef.current.src = URL.createObjectURL(blob);
    }
  }, [styleGuide, selectedComponent, previewMode]);

  const handleColorChange = (colorKey, value) => {
    setStyleGuide(prev => ({
      ...prev,
      colors: { ...prev.colors, [colorKey]: value }
    }));
  };

  const handleTypographyChange = (type, key, value) => {
    setStyleGuide(prev => ({
      ...prev,
      typography: {
        ...prev.typography,
        [type]: { ...prev.typography[type], [key]: value }
      }
    }));
  };

  const handleButtonChange = (key, value) => {
    setStyleGuide(prev => ({
      ...prev,
      ui: {
        ...prev.ui,
        button: { ...prev.ui.button, [key]: value }
      }
    }));
  };

  return (
    <div className="container-fluid py-4">
      <div className="row">
        {/* Sidebar - Style Guide Editor */}
        <div className="col-lg-3">
          <div className="card shadow-sm">
            <div className="card-header bg-dark text-white">
              <h5 className="mb-0">Style Guide</h5>
            </div>
            <div className="card-body">
              {/* Colors */}
              <h6 className="text-uppercase small fw-bold text-muted mb-3">Colors</h6>
              <div className="mb-3">
                <label className="form-label small">Primary</label>
                <div className="d-flex gap-2">
                  <input
                    type="color"
                    className="form-control form-control-color"
                    value={styleGuide.colors.primary}
                    onChange={(e) => handleColorChange('primary', e.target.value)}
                  />
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={styleGuide.colors.primary}
                    onChange={(e) => handleColorChange('primary', e.target.value)}
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label small">Secondary</label>
                <div className="d-flex gap-2">
                  <input
                    type="color"
                    className="form-control form-control-color"
                    value={styleGuide.colors.secondary}
                    onChange={(e) => handleColorChange('secondary', e.target.value)}
                  />
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={styleGuide.colors.secondary}
                    onChange={(e) => handleColorChange('secondary', e.target.value)}
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label small">Background</label>
                <div className="d-flex gap-2">
                  <input
                    type="color"
                    className="form-control form-control-color"
                    value={styleGuide.colors.background}
                    onChange={(e) => handleColorChange('background', e.target.value)}
                  />
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={styleGuide.colors.background}
                    onChange={(e) => handleColorChange('background', e.target.value)}
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label small">Text</label>
                <div className="d-flex gap-2">
                  <input
                    type="color"
                    className="form-control form-control-color"
                    value={styleGuide.colors.text}
                    onChange={(e) => handleColorChange('text', e.target.value)}
                  />
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={styleGuide.colors.text}
                    onChange={(e) => handleColorChange('text', e.target.value)}
                  />
                </div>
              </div>

              <hr />

              {/* Typography */}
              <h6 className="text-uppercase small fw-bold text-muted mb-3">Typography</h6>
              <div className="mb-3">
                <label className="form-label small">Heading Font</label>
                <select
                  className="form-select form-select-sm"
                  value={styleGuide.typography.heading.font_family}
                  onChange={(e) => handleTypographyChange('heading', 'font_family', e.target.value)}
                >
                  <option value="Outfit">Outfit</option>
                  <option value="Sora">Sora</option>
                  <option value="Plus Jakarta Sans">Plus Jakarta Sans</option>
                  <option value="Manrope">Manrope</option>
                  <option value="DM Sans">DM Sans</option>
                  <option value="Poppins">Poppins</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label small">Body Font</label>
                <select
                  className="form-select form-select-sm"
                  value={styleGuide.typography.body.font_family}
                  onChange={(e) => handleTypographyChange('body', 'font_family', e.target.value)}
                >
                  <option value="DM Sans">DM Sans</option>
                  <option value="Inter">Inter</option>
                  <option value="Outfit">Outfit</option>
                  <option value="Manrope">Manrope</option>
                  <option value="Sora">Sora</option>
                </select>
              </div>

              <hr />

              {/* Button Styling */}
              <h6 className="text-uppercase small fw-bold text-muted mb-3">Button Style</h6>
              <div className="mb-3">
                <label className="form-label small">Border Radius</label>
                <select
                  className="form-select form-select-sm"
                  value={styleGuide.ui.button.border_radius}
                  onChange={(e) => handleButtonChange('border_radius', e.target.value)}
                >
                  <option value="0px">Square (0px)</option>
                  <option value="6px">Subtle (6px)</option>
                  <option value="10px">Rounded (10px)</option>
                  <option value="12px">Modern (12px)</option>
                  <option value="16px">Soft (16px)</option>
                  <option value="50px">Pill (50px)</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label small">Padding</label>
                <select
                  className="form-select form-select-sm"
                  value={styleGuide.ui.button.padding}
                  onChange={(e) => handleButtonChange('padding', e.target.value)}
                >
                  <option value="10px 20px">Compact</option>
                  <option value="12px 24px">Medium</option>
                  <option value="14px 28px">Comfortable</option>
                  <option value="16px 32px">Spacious</option>
                </select>
              </div>

              <hr />

              <button
                className="btn btn-outline-secondary btn-sm w-100"
                onClick={() => setStyleGuide(defaultStyleGuide)}
              >
                Reset to Defaults
              </button>
            </div>
          </div>

          {/* Component Selector */}
          <div className="card shadow-sm mt-3">
            <div className="card-header bg-dark text-white">
              <h5 className="mb-0">Components</h5>
            </div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush">
                <button
                  className={`list-group-item list-group-item-action ${previewMode === 'all' ? 'active' : ''}`}
                  onClick={() => {
                    setPreviewMode('all');
                    setSelectedComponent(null);
                  }}
                >
                  <i className="bi bi-grid me-2"></i>
                  View All Components
                </button>
                {componentList.map((comp) => (
                  <button
                    key={comp.component_code}
                    className={`list-group-item list-group-item-action ${
                      previewMode === 'single' && selectedComponent?.component_code === comp.component_code ? 'active' : ''
                    }`}
                    onClick={() => {
                      setPreviewMode('single');
                      setSelectedComponent(comp);
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <span className="badge bg-secondary me-2">{comp.component_code}</span>
                        {comp.title}
                      </div>
                    </div>
                    <small className="text-muted d-block">{comp.description}</small>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Preview Area */}
        <div className="col-lg-9">
          <div className="card shadow-sm">
            <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                Preview
                {selectedComponent && previewMode === 'single' && (
                  <span className="badge bg-primary ms-2">{selectedComponent.component_code}</span>
                )}
              </h5>
              <div className="btn-group btn-group-sm">
                <button className="btn btn-outline-light" title="Desktop">
                  <i className="bi bi-display"></i>
                </button>
                <button className="btn btn-outline-light" title="Tablet">
                  <i className="bi bi-tablet"></i>
                </button>
                <button className="btn btn-outline-light" title="Mobile">
                  <i className="bi bi-phone"></i>
                </button>
              </div>
            </div>
            <div className="card-body p-0" style={{ height: 'calc(100vh - 200px)', overflow: 'hidden' }}>
              <iframe
                ref={iframeRef}
                title="Component Preview"
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </div>
          </div>

          {/* Component Details */}
          {selectedComponent && previewMode === 'single' && (
            <div className="card shadow-sm mt-3">
              <div className="card-header">
                <h6 className="mb-0">Placeholder Schema - {selectedComponent.component_code}</h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="text-muted small">Editable Fields</h6>
                    <table className="table table-sm table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>Field</th>
                          <th>Type</th>
                          <th>Label</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(selectedComponent.placeholder_json).map(([key, value]) => (
                          <tr key={key}>
                            <td><code>{`{{${key}}}`}</code></td>
                            <td><span className="badge bg-info">{value.type}</span></td>
                            <td>{value.label}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-muted small">Sample Data</h6>
                    <pre className="bg-light p-2 rounded" style={{ fontSize: '0.75rem', maxHeight: '300px', overflow: 'auto' }}>
                      {JSON.stringify(selectedComponent.sample_data_json, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComponentPreview;
